import apiClient from './api';

export interface AgentVacation {
  id: number;
  agent: {
    id: number;
    nom: string;
    prenom: string;
    nomComplet: string;
    email: string;
    roles: string[];
  };
  dateDebut: string;
  dateFin: string;
  heureDebut: string;
  heureFin: string;
  heureDebutReelle?: string;
  heureFinReelle?: string;
  statut: 'planifiee' | 'en_cours' | 'terminee' | 'annulee';
  commentaire?: string;
  isVacationNuit: boolean;
  dureePrevueHeures: number;
  dureeReelleHeures?: number;
  plageHoraire: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateVacationDTO {
  agent_id: number;
  date_debut: string;
  date_fin: string;
  heure_debut: string;
  heure_fin: string;
  commentaire?: string;
}

export interface CreateVacationPeriodeDTO {
  agent_id: number;
  periode_debut: string;
  periode_fin: string;
  heure_debut: string;
  heure_fin: string;
  jours?: number[]; // 1=lundi, 7=dimanche
  commentaire?: string;
}

export interface CreatePeriodeResult {
  success: boolean;
  created: number;
  skipped: number;
  errors: string[];
  message: string;
  vacations: {
    id: number;
    dateDebut: string;
    dateFin: string;
    plageHoraire: string;
  }[];
}

export interface UpdateVacationDTO {
  date_debut?: string;
  date_fin?: string;
  heure_debut?: string;
  heure_fin?: string;
  commentaire?: string;
}

export interface VacationFilters {
  date?: string;
  date_debut?: string;
  date_fin?: string;
  agent_id?: number;
}

export interface PlanningAgent {
  agent: {
    id: number;
    nom: string;
    prenom: string;
    nomComplet: string;
  };
  vacations: {
    id: number;
    heureDebut: string;
    heureFin: string;
    dateDebut: string;
    dateFin: string;
    statut: string;
    statutLabel: string;
    isVacationNuit: boolean;
    plageHoraire: string;
    dureePrevueHeures: number;
  }[];
}

export interface PlanningResponse {
  date: string;
  planning: PlanningAgent[];
}

export interface VacationStats {
  periode: {
    debut: string;
    fin: string;
  };
  stats: {
    total: number;
    planifiees: number;
    en_cours: number;
    terminees: number;
    annulees: number;
  };
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errors: string[];
  message: string;
}

export const vacationService = {
  /**
   * Liste des vacations avec filtres
   */
  async list(filters: VacationFilters = {}): Promise<AgentVacation[]> {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.date_debut) params.append('date_debut', filters.date_debut);
    if (filters.date_fin) params.append('date_fin', filters.date_fin);
    if (filters.agent_id) params.append('agent_id', String(filters.agent_id));

    const response = await apiClient.get<AgentVacation[]>(`/vacations?${params.toString()}`);
    return response.data;
  },

  /**
   * Planning du jour
   */
  async getPlanning(date?: string): Promise<PlanningResponse> {
    const params = date ? `?date=${date}` : '';
    const response = await apiClient.get<PlanningResponse>(`/vacations/planning${params}`);
    return response.data;
  },

  /**
   * Detail d'une vacation
   */
  async get(id: number): Promise<AgentVacation> {
    const response = await apiClient.get<AgentVacation>(`/vacations/${id}`);
    return response.data;
  },

  /**
   * Creation d'une vacation
   */
  async create(data: CreateVacationDTO): Promise<AgentVacation> {
    const response = await apiClient.post<AgentVacation>('/vacations', data);
    return response.data;
  },

  /**
   * Creation de vacations sur une periode (plusieurs jours)
   */
  async createPeriode(data: CreateVacationPeriodeDTO): Promise<CreatePeriodeResult> {
    const response = await apiClient.post<CreatePeriodeResult>('/vacations/periode', data);
    return response.data;
  },

  /**
   * Modification d'une vacation
   */
  async update(id: number, data: UpdateVacationDTO): Promise<AgentVacation> {
    const response = await apiClient.put<AgentVacation>(`/vacations/${id}`, data);
    return response.data;
  },

  /**
   * Suppression d'une vacation
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/vacations/${id}`);
  },

  /**
   * Demarrer une vacation
   */
  async demarrer(id: number): Promise<{ success: boolean; heureDebutReelle: string }> {
    const response = await apiClient.post<{ success: boolean; heureDebutReelle: string }>(
      `/vacations/${id}/demarrer`
    );
    return response.data;
  },

  /**
   * Terminer une vacation
   */
  async terminer(id: number): Promise<{ success: boolean; heureFinReelle: string; dureeReelleHeures: number }> {
    const response = await apiClient.post<{ success: boolean; heureFinReelle: string; dureeReelleHeures: number }>(
      `/vacations/${id}/terminer`
    );
    return response.data;
  },

  /**
   * Annuler une vacation
   */
  async annuler(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(`/vacations/${id}/annuler`);
    return response.data;
  },

  /**
   * Import depuis un fichier Excel/CSV
   */
  async importFromFile(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ImportResult>('/vacations/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Telecharge le modele d'import
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get('/vacations/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Statistiques des vacations
   */
  async getStats(dateDebut?: string, dateFin?: string): Promise<VacationStats> {
    const params = new URLSearchParams();
    if (dateDebut) params.append('date_debut', dateDebut);
    if (dateFin) params.append('date_fin', dateFin);

    const response = await apiClient.get<VacationStats>(`/vacations/stats?${params.toString()}`);
    return response.data;
  },
};

export default vacationService;
